import clsx from "clsx";
import { _Product, _Variant, useCartDrawer, useGlobalProducts, useGlobalSettings } from "global-stores";
import { MinusIcon, PlusIcon, TimerIcon } from "icons";
import { Image } from "image";
import React, { FC, useEffect, useMemo, useState } from "react";
import { _Cart_fetch_api_Items } from "types";
import { capitalize } from "utils";
import { CONFIGURABLE_PRODUCTS_LINE_ITEM_UUID_CHILD_KEY, CONFIGURABLE_PRODUCTS_LINE_ITEM_UUID_PARENT_KEY } from "./product-configurable-products.constants";

export const CartLineItems = () => {
  const { cartData } = useCartDrawer(({ cartData }) => ({ cartData }));

  // Get all items including children for proper nesting
  const allItems = cartData?.items?.filter(item => item?.quantity > 0) || [];
  
  // Separate parent and child items
  const parentItems = allItems.filter(item => !item.properties[CONFIGURABLE_PRODUCTS_LINE_ITEM_UUID_CHILD_KEY]);
  const childItems = allItems.filter(item => item.properties[CONFIGURABLE_PRODUCTS_LINE_ITEM_UUID_CHILD_KEY]);

  return (
    <>
      {parentItems?.length
        ? <>
            {parentItems.map((line_item, index) => {
              const parentUuid = line_item.properties[CONFIGURABLE_PRODUCTS_LINE_ITEM_UUID_PARENT_KEY];
              const relatedChildren = parentUuid 
                ? childItems.filter(child => child.properties[CONFIGURABLE_PRODUCTS_LINE_ITEM_UUID_CHILD_KEY] === parentUuid)
                : [];
              
              return (
                <React.Fragment key={line_item?.key}>
                  <CartLineItem
                    line_item={line_item}
                    index={index}
                    line_items={allItems}
                    isFirstParent={index === 0}
                  />
                  {/* Render child items nested under parent */}
                  {relatedChildren.map((childItem, childIndex) => (
                    <CartLineItem
                      key={childItem?.key}
                      line_item={childItem}
                      index={allItems.findIndex(item => item.key === childItem.key)}
                      line_items={allItems}
                      isChildItem={true}
                      isLastChild={childIndex === relatedChildren.length - 1}
                    />
                  ))}
                </React.Fragment>
              );
            })}
          </>
        : null}
    </>
  );
};

export const CartLineItem: FC<{
  line_item: _Cart_fetch_api_Items;
  index: number;
  line_items: _Cart_fetch_api_Items[];
  isChildItem?: boolean;
  isFirstParent?: boolean;
  isLastChild?: boolean;
}> = ({ line_items, line_item, index, isChildItem = false, isFirstParent = false, isLastChild = false }) => {
  const { adjustItem, routeProtectionProducts } = useCartDrawer(
    ({ adjustItem, routeProtectionProducts }) => ({ adjustItem, routeProtectionProducts })
  );
  const [lineItemProduct, setLineItemProduct] = useState<_Product>(null);
  const [lineItemVariant, setLineItemVariant] = useState<_Variant>(null);
  const { hydrated } = useGlobalProducts(({ hydrated }) => ({ hydrated }));

  const {
    product_data__preorder_metafield,
    product_data__preorder_limit_metafield,
    cart_order_discounts,
  } = useGlobalSettings(
    ({
      product_data__preorder_metafield,
      product_data__preorder_limit_metafield,
      cart_order_discounts,
    }) => ({
      product_data__preorder_metafield,
      product_data__preorder_limit_metafield,
      cart_order_discounts,
    })
  );

  const routeProtection = routeProtectionProducts.find(
    (block) => block?.products?.[0]?.handle === line_item?.handle
  );

  const isSingleQuantityRouteProtectionItem = !!routeProtection?.limit_quantity;

  const disableIncreaseQuantity = useMemo(() => {
    if (!lineItemVariant) return false;
    const { inventory_management, inventory_policy, inventory_quantity } = lineItemVariant;

    const quantityInCart = line_items?.reduce((acc, item) => {
      if (item.variant_id === line_item?.variant_id) {
        acc += item.quantity;
      }
      return acc;
    }, 0);

    const continueSelling = inventory_policy === "continue" || inventory_management !== "shopify";

    if (!continueSelling && quantityInCart >= inventory_quantity) {
      return true;
    }

    if (continueSelling) {
      const [preorder_ns, preorder_key] = product_data__preorder_metafield.split(".");
      const [limit_ns, limit_key] = product_data__preorder_limit_metafield.split(".");

      const pre_order =
        lineItemVariant?.metafields?.[preorder_ns]?.[preorder_key] ??
        lineItemProduct?.metafields?.[preorder_ns]?.[preorder_key];

      const pre_order_limit = +(lineItemVariant?.metafields?.[limit_ns]?.[limit_key] ?? 0);

      if (pre_order && quantityInCart >= pre_order_limit + inventory_quantity) {
        return true;
      }
    }

    return false;
  }, [
    line_item?.variant_id,
    line_items,
    lineItemProduct?.metafields,
    product_data__preorder_limit_metafield,
    product_data__preorder_metafield,
    lineItemVariant,
  ]);

  const final_price =
    cart_order_discounts === "inline" ? line_item?.discounted_price : line_item?.final_price;

  useEffect(() => {
    if ((!lineItemVariant || !lineItemProduct) && hydrated) {
      window.useGlobalProducts
        .getState()
        .getAsyncProduct(line_item?.handle)
        .then((p) => {
          setLineItemVariant(p?.variants?.find(({ id }) => id === line_item?.variant_id));
          setLineItemProduct(p);
        });
    }
  }, [hydrated, line_item?.handle, line_item?.variant_id, lineItemProduct, lineItemVariant]);

  return (
    <tr className={clsx(
      "group hf:bg-gray-50",
      isChildItem && "bg-gray-25 border-l border-gray-300",
      isChildItem && !isLastChild && "border-b border-gray-200" // Bottom line for children, except the last one
    )}>
      <td className={clsx(
        "py-4 pr-3 text-sm",
        isChildItem ? "pl-8" : "pl-4 sm:pl-6"
      )}>
        <div className="grid w-full items-center gap-4 sm:grid-cols-[100px_1fr]">
          <div className={clsx(
            "relative",
            isChildItem ? "w-[60px]" : "w-[100px]"
          )}>
            <Image
              src={`${line_item?.image}&crop=center&height=200&width=200`}
              alt={line_item?.featured_image?.alt ?? line_item?.title}
              width={200}
              height={200}
              className={clsx(
                "w-full object-contain",
                !line_item?.image && "pointer-events-none hidden overflow-hidden opacity-0"
              )}
            />
          </div>

          {isChildItem ? (
            <div className="block flex-1">
              <h3 className="text-sm text-gray-600 font-semibold">
                {line_item?.product_title}
              </h3>
              <h4 className="text-xs uppercase tracking-wide text-gray-500">
                {line_item?.variant_title}
              </h4>
              
              <div className="mt-1 text-sm font-medium">
                <span className="font-bold text-gray-600" dangerouslySetInnerHTML={{ __html: window.formatMoney(final_price) }}></span>
              </div>
            </div>
          ) : (
            <a className="block flex-1" href={line_item?.url}>
              <h3 className="font-semibold group-hf:underline">
                {line_item?.product_title}
              </h3>
              <h4 className="text-xs uppercase tracking-wide text-gray-500">
                {line_item?.variant_title}
              </h4>

              {/* Show properties only for parent items and filter out extra options */}
              {Object.entries(line_item?.properties ?? {})
                ?.filter(([key]) => !/^_/gi.test(key))
                ?.map(([key, value]) => {
                  if (key === "preorder") {
                    return (
                      <p key={key} className="mt-1 flex text-xs text-gray-600 opacity-70">
                        <TimerIcon className="mr-2 h-4 w-4" /> <span>{value}</span>
                      </p>
                    );
                  }
                  return (
                    <p key={key} className="text-xs text-gray-600">
                      {capitalize(key)}: {value}
                    </p>
                  );
                })}

              <div className="mt-1 text-sm font-medium">
                {isSingleQuantityRouteProtectionItem
                  ? null
                  : <span
                      className={clsx(
                        "text-theme-text/50 line-through",
                        line_item?.original_price === final_price &&
                          "pointer-events-none hidden overflow-hidden opacity-0"
                      )}
                      dangerouslySetInnerHTML={{
                        __html: `${window.formatMoney(line_item?.original_price)} `,
                      }}
                    ></span>}

                <span className="font-bold text-primary-outline" dangerouslySetInnerHTML={{ __html: window.formatMoney(final_price) }}></span>
              </div>
            </a>
          )}
          
          {line_item?.discounts?.length && final_price === line_item?.discounted_price && !isChildItem
            ? <div className="text-xs font-medium opacity-50">
                {line_item?.discounts?.map((discount) => (
                  <div key={discount.title}>Discount: {discount.title}</div>
                ))}
              </div>
            : null}
        </div>
      </td>
      <td className="whitespace-nowrap px-4 py-2 text-sm text-gray-500 max-md:hidden sm:px-6">
        {line_item?.sku}
      </td>
      <td className="whitespace-nowrap px-4 py-2 text-right text-sm text-gray-500 max-sm:hidden sm:px-6">
        {isSingleQuantityRouteProtectionItem
          ? null
          : !isChildItem && (
              <div className="relative flex">
                <div className="mr-auto mt-2 flex select-none items-center space-x-2.5 rounded-theme border border-gray-300 px-1.5 py-0.5 text-[13px] font-semibold">
                  <button
                    type="button"
                    className="flex items-center justify-center p-1.5 text-gray-600 hf:text-gray-900"
                    onClick={() => adjustItem(line_item, -1, index)}
                  >
                    <span className="sr-only">Decrease quantity by 1</span>
                    <MinusIcon className="h-2.5 w-2.5" />
                  </button>
                  <span className="text-gray-800">{line_item?.quantity}</span>
                  <button
                    type="button"
                    onClick={() => adjustItem(line_item, 1, index)}
                    className="flex items-center justify-center p-1.5 text-gray-600 hf:text-gray-900"
                    disabled={disableIncreaseQuantity || isSingleQuantityRouteProtectionItem}
                  >
                    <span className="sr-only">Increase quantity by 1</span>
                    <PlusIcon className="h-2.5 w-2.5" />
                  </button>
                </div>
                <span className="absolute left-0 top-full mt-0.5 block w-full text-center text-xs">
                  <button
                    className="text-gray-400 hf:text-gray-500 hf:underline"
                    onClick={() => adjustItem(line_item, -line_item?.quantity, index)}
                  >
                    Remove
                  </button>
                </span>
              </div>
            )}
      </td>
      <td className="whitespace-nowrap px-4 py-2 text-right text-sm font-medium text-gray-700 sm:px-6">
        <span
          dangerouslySetInnerHTML={{ __html: window.formatMoney(line_item?.final_line_price) }}
        ></span>
        <div className="mb-auto mt-4 sm:hidden">
          {isSingleQuantityRouteProtectionItem
            ? null
            : !isChildItem && (
                <div className="relative flex">
                  <div className="mr-auto mt-2 flex select-none items-center space-x-2.5 rounded-theme border border-gray-300 px-1.5 py-0.5 text-[13px] font-semibold">
                    <button
                      type="button"
                      className="flex items-center justify-center p-1.5 text-gray-600 hf:text-gray-900"
                      onClick={() => adjustItem(line_item, -1, index)}
                    >
                      <span className="sr-only">Decrease quantity by 1</span>
                      <MinusIcon className="h-2.5 w-2.5" />
                    </button>
                    <span className="text-gray-800">{line_item?.quantity}</span>
                    <button
                      type="button"
                      onClick={() => adjustItem(line_item, 1, index)}
                      className="flex items-center justify-center p-1.5 text-gray-600 hf:text-gray-900"
                      disabled={disableIncreaseQuantity || isSingleQuantityRouteProtectionItem}
                    >
                      <span className="sr-only">Increase quantity by 1</span>
                      <PlusIcon className="h-2.5 w-2.5" />
                    </button>
                  </div>
                  <span className="absolute left-0 top-full mt-0.5 block w-full text-center text-xs">
                    <button
                      className="text-gray-400 hf:text-gray-500 hf:underline"
                      onClick={() => adjustItem(line_item, -line_item?.quantity, index)}
                    >
                      Remove
                    </button>
                  </span>
                </div>
              )}
        </div>
      </td>
    </tr>
  );
};
